/*
 * Auryaa motion foundation
 * One GSAP clock drives Lenis smooth scroll and every ScrollTrigger.
 * Rules:
 *  - Animate only transform, opacity, and clip-path.
 *  - Respect prefers-reduced-motion. Content stays visible without JS.
 *  - Re-initialise per section on Shopify theme editor events.
 *  - Never hide the LCP image. Reveals run on elements below the hero only,
 *    or on text layered above the hero media.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGsap = typeof window.gsap !== 'undefined';
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  html.classList.add('au-js');
  if (reduced) html.classList.add('au-reduced-motion');

  if (!hasGsap || reduced) {
    // No animation. Make sure nothing stays hidden.
    if (!hasGsap) html.classList.remove('au-js');
    document.querySelectorAll('[data-au-split]').forEach(function (el) {
      el.classList.add('au-split-ready');
    });
    window.AuryaaMotion = { init: function () {}, refresh: function () {}, lenis: null, reduced: reduced };
    document.dispatchEvent(new CustomEvent('au:motion-ready'));
    return;
  }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var SplitText = window.SplitText;
  if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  gsap.defaults({ ease: 'power3.out', duration: 0.9 });

  /* ---------- Lenis smooth scroll (desktop pointer devices only) ---------- */
  var lenis = null;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (window.Lenis && finePointer && !designMode) {
    lenis = new window.Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2
    });
    if (ScrollTrigger) lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    // Anchor links go through Lenis so the header offset stays correct.
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href') === '#') return;
      var target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -parseInt(getComputedStyle(html).getPropertyValue('--au-nav-height') || '0', 10) });
    });

    // Drawers and modals must not scroll the page behind them.
    document.addEventListener('au:lock-scroll', function () { lenis.stop(); });
    document.addEventListener('au:unlock-scroll', function () { lenis.start(); });
  }

  /* ---------- Primitives ---------- */
  function revealItems(scope) {
    scope.querySelectorAll('[data-au-reveal]').forEach(function (el) {
      if (el.__auDone) return;
      el.__auDone = true;
      var delay = parseFloat(el.getAttribute('data-au-delay') || '0');
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: delay,
        ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true }
      });
    });
  }

  function imageReveals(scope) {
    scope.querySelectorAll('[data-au-image-reveal]').forEach(function (el) {
      if (el.__auDone) return;
      el.__auDone = true;
      var media = el.querySelector('img, video');
      var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
      tl.to(el, { clipPath: 'inset(0 0 0% 0)', duration: 1.3, ease: 'expo.out' }, 0);
      if (media) tl.to(media, { scale: 1, duration: 1.6, ease: 'expo.out' }, 0);
    });
  }

  function splitReveals(scope) {
    if (!SplitText) {
      scope.querySelectorAll('[data-au-split]').forEach(function (el) { el.classList.add('au-split-ready'); });
      return;
    }
    scope.querySelectorAll('[data-au-split]').forEach(function (el) {
      if (el.__auDone) return;
      el.__auDone = true;
      var mode = el.getAttribute('data-au-split') || 'lines';
      var immediate = el.hasAttribute('data-au-immediate');
      var split = new SplitText(el, { type: mode === 'chars' ? 'lines,chars' : 'lines', linesClass: 'line', mask: 'lines', autoSplit: true });
      el.classList.add('au-split-ready');
      var targets = mode === 'chars' ? split.chars : split.lines;
      var vars = {
        yPercent: 110,
        opacity: mode === 'chars' ? 0 : 1,
        duration: mode === 'chars' ? 0.9 : 1.2,
        stagger: mode === 'chars' ? 0.018 : 0.09,
        ease: 'expo.out',
        delay: parseFloat(el.getAttribute('data-au-delay') || '0')
      };
      if (!immediate) vars.scrollTrigger = { trigger: el, start: 'top 85%', once: true };
      gsap.from(targets, vars);
    });
  }

  function parallax(scope) {
    scope.querySelectorAll('[data-au-parallax]').forEach(function (el) {
      if (el.__auDone) return;
      el.__auDone = true;
      var amount = parseFloat(el.getAttribute('data-au-parallax') || '12');
      gsap.fromTo(el, { yPercent: -amount }, {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: 1 }
      });
    });
  }

  function init(scope) {
    scope = scope || document;
    splitReveals(scope);
    revealItems(scope);
    imageReveals(scope);
    parallax(scope);
  }

  function refresh() {
    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  /* ---------- Boot ---------- */
  function boot() {
    init(document);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
    window.addEventListener('load', refresh);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ---------- Shopify theme editor ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    refresh();
  });
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:unload', function () {
    if (ScrollTrigger) ScrollTrigger.getAll().forEach(function (t) { if (!document.contains(t.trigger)) t.kill(); });
    refresh();
  });

  window.AuryaaMotion = { init: init, refresh: refresh, lenis: lenis, reduced: false, gsap: gsap };
  document.dispatchEvent(new CustomEvent('au:motion-ready'));
})();
